import React, { useState, useRef, useCallback, useEffect } from 'react';
import { approveItem, rejectItem, setSignaturePlacement, allowlistFormType, approveRefill, reprocessItem, getSignPreview } from '../lib/fax-api';
import { FaxImagePreview, FaxEditablePagePreview } from './fax-image-preview.jsx';

// Clamp helper: keep a number inside [lo, hi].
function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }

// Normalize one box so it stays fully inside the page: x,y,w,h in [0,1] and
// x+w<=1 / y+h<=1. A tiny minimum size keeps a box draggable/visible. This
// mirrors the server's strict validator so a save NEVER 400s.
const MIN_BOX = 0.02;
function normalizeBox(box) {
  let w = clamp(Number(box.w) || 0, MIN_BOX, 1);
  let h = clamp(Number(box.h) || 0, MIN_BOX, 1);
  let x = clamp(Number(box.x) || 0, 0, 1 - w);
  let y = clamp(Number(box.y) || 0, 0, 1 - h);
  return { x, y, w, h };
}

export function FaxReviewItem({ item, onChange }) {
  const extract = item.raw_extract || {};
  const pt = extract.patient || {};
  const doc = extract.document || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function approve() {
    setBusy(true); setError(null);
    try {
      const result = await approveItem(item.elation_group_id);
      const outcome = result && result.approve_outcome;
      if (outcome === 'filed' || outcome === 'gone' || outcome === 'non_patient') {
        onChange();
      } else {
        // still_review / failed — keep the item visible with the new reason.
        const reason = (result && result.approve_reason) || 'approve did not file — still needs review';
        setError(reason);
        onChange();
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function reject() {
    const reason = window.prompt('Reject reason (optional)?', '');
    if (reason === null) return;
    setBusy(true); setError(null);
    try { await rejectItem(item.elation_group_id, reason); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function reprocess() {
    setBusy(true); setError(null);
    try { await reprocessItem(item.elation_group_id); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const failures = (item.validation_summary && item.validation_summary.failures) || [];
  const confidence = pt.id_confidence != null ? Math.round(pt.id_confidence * 100) + '%' : '?';
  const reviewReason = item.validation_summary && item.validation_summary.reason;
  const nameLine = [pt.last_name, pt.first_name].filter(Boolean).join(', ') || '(no name)';

  return (
    <div className="fax-review-card">
      <FaxImagePreview
        groupId={item.elation_group_id}
        imageId={item.first_image_id}
        alt={doc.one_line_summary || 'fax page'}
      />
      <div className="fax-review-card-body">
        <div className="fax-review-card-title">{doc.one_line_summary || '(no summary)'}</div>
        <div className="fax-review-card-meta">
          <strong>{nameLine}</strong>
          <span> — DOB {pt.dob || '?'}</span>
          <span> · {item.suggested_document_type_label || item.suggested_document_type || 'Misc'}</span>
          <span> · id-conf <span className="tabnum">{confidence}</span></span>
        </div>
        {reviewReason && (
          <div className="fax-review-card-reason">{reviewReason}</div>
        )}
        {failures.length > 0 && (
          <ul className="fax-review-card-failures">
            {failures.map((f, i) => <li key={i}>⚠ {f.field}: {f.reason}</li>)}
          </ul>
        )}
        {error && <div className="fax-review-card-error">Error: {error}</div>}
      </div>
      <div className="fax-review-card-actions">
        <button className="btn accent sm" disabled={busy} onClick={approve}>Approve</button>
        <button
          className="btn ghost sm"
          disabled={busy}
          title="Re-run through triage under current automation settings"
          onClick={reprocess}
        >
          Reprocess
        </button>
        <button className="btn ghost sm" disabled={busy} onClick={reject}>Reject</button>
      </div>
    </div>
  );
}

// Review card for a signature-form ("signature_pending") item. Same overall
// structure as FaxReviewItem (preview + body + Approve/Reject), but it
// surfaces the form subtype + signer status and, when there is no eligible
// signer, disables Approve and shows the reason.
//
// IMPORTANT: Approve hits the SAME backend route (approveItem →
// POST /api/fax-inbox/:groupId/approve), which special-cases
// suggested_classification === 'signature_request' to sign + fax the form
// back (Task 8). We do NOT build a parallel approve path. The signature
// branch of that route returns { ok, signed, reportId, letterId, sent } on
// success (NOT the approve_outcome shape the non-signature branch returns),
// and throws via api.post (409/500 { error }) when the signer is ineligible
// or signing fails — in which case the row stays in review.
export function FaxSignaturePendingCard({ item, onChange, currentUser }) {
  const extract = item.raw_extract || {};
  const pt = extract.patient || {};
  const doc = extract.document || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Adjustable signature placement. Initialized from the stored jsonb array
  // (guard null/empty → []). The approve route stamps whatever is stored, so
  // each drag/resize persists the new geometry via setSignaturePlacement.
  const [placement, setPlacement] = useState(() =>
    Array.isArray(item.placement) ? item.placement : []);
  const [placeError, setPlaceError] = useState(null);
  const [placeSaved, setPlaceSaved] = useState(false);

  // Admin-only curation: add this subtype to the auto-sign allowlist.
  const isAdmin = String((currentUser && currentUser.role) || '').toLowerCase() === 'admin';
  const subtypeKey = item.subtype_key || '';
  const [allowBusy, setAllowBusy] = useState(false);
  const [allowDone, setAllowDone] = useState(false);

  // Signed-PDF preview: server-rendered via the SAME path Approve & sign stamps
  // (writers.renderSignedPdf), so the reviewer sees every page — including
  // signature boxes on pages the page-1 placement editor can't show — before
  // the irreversible send. Fetched on demand (it re-pulls the fax pages from
  // Elation); a placement save invalidates it since the stamp would move.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);
  useEffect(() => () => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
  }, []);
  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    setPreviewUrl(null); setPreviewError(null);
  }, []);

  async function loadPreview() {
    setPreviewLoading(true); setPreviewError(null);
    try {
      const r = await getSignPreview(item.elation_group_id);
      // Best-effort contract: a render/signer/placement miss is HTTP 200 { error }.
      if (r && r.error) { clearPreview(); setPreviewError(r.error); return; }
      let url = null;
      if (r && r.pdfBase64) {
        try { url = URL.createObjectURL(new Blob([Uint8Array.from(atob(r.pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' })); } catch (_) { url = null; }
      }
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      if (!url) setPreviewError('Could not render the preview.');
    } catch (e) {
      clearPreview(); setPreviewError(e.message);
    } finally { setPreviewLoading(false); }
  }

  // signing_user_id is the chart-PCP signer pre-resolved at park time (a CCM
  // user id, text — the queue row carries no signer display name). It is
  // INFORMATIONAL ONLY: the server re-resolves the signer at approve time from
  // the APPROVING clinician (CCM app user = signer, chart PCP as fallback), so
  // a null here must NOT block Approve.
  const hasSigner = item.signing_user_id != null && item.signing_user_id !== '';
  // The locate step may have found NO signature box (placement parked as []),
  // even when the PCP is otherwise an eligible signer. Stamping nothing yet
  // filing/faxing a "Signed: …" PDF would be an unsigned form misrepresented as
  // signed, so the server rejects it (409). Mirror that here: require a non-empty
  // placement before Approve, and show why when it's missing.
  const hasPlacement = Array.isArray(item.placement) && item.placement.length > 0;
  const reason = (item.validation_summary && item.validation_summary.reason) || null;
  // The row was parked because the PRACTICE-level sign-forms automation is OFF
  // (the worker never tried to resolve a signer). This is NOT a genuine
  // "no eligible signer" — show a single clear "turn it on / sign in Elation"
  // message instead of the no-signer line + allowlist button.
  const signFormsDisabled = reason === 'fax sign-forms disabled';

  // Persist the (already-clamped) placement on drag/resize END. The editor
  // calls this on pointer-up only, never mid-drag.
  const savePlacement = useCallback(async (next) => {
    setPlaceError(null); setPlaceSaved(false);
    try {
      await setSignaturePlacement(item.elation_group_id, next);
      setPlaceSaved(true);
      // The stamp will now land somewhere else — a rendered preview is stale.
      clearPreview();
    } catch (e) {
      setPlaceError(e.message);
    }
  }, [item.elation_group_id, clearPreview]);

  async function allowlist() {
    if (!subtypeKey) return;
    setAllowBusy(true); setError(null);
    try {
      await allowlistFormType(subtypeKey);
      setAllowDone(true);
    } catch (e) {
      setError(e.message);
    } finally { setAllowBusy(false); }
  }

  async function approve() {
    // Guard: never POST without a non-empty placement (server returns 409), and
    // never when the practice's sign-forms automation is off. An absent parked
    // signer does NOT block — the server signs as the approving clinician.
    if (!hasPlacement || signFormsDisabled) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      // Same route the normal review card uses. Signature branch returns
      // { ok, signed, reportId, letterId, sent } on a 2xx.
      const result = await approveItem(item.elation_group_id);
      if (result && result.signed) {
        setNotice(result.sent
          ? 'Signed and faxed back to the sender.'
          : 'Signed and filed to the chart (return fax not sent).');
        onChange(); // refresh — the row is now 'approved' and drops off the queue.
      } else {
        // Defensive: a 2xx without signed:true shouldn't happen for this branch,
        // but if it does, keep the item visible and surface whatever came back.
        setError((result && result.error) || 'approve did not complete the signature — still needs review');
        onChange();
      }
    } catch (e) {
      // 409 (ineligible signer) / 500 (signing failed): the row stays in
      // review on the server. Surface the error; do NOT refresh it away.
      setError(e.message);
    } finally { setBusy(false); }
  }

  async function reject() {
    const r = window.prompt('Reject reason (optional)?', '');
    if (r === null) return;
    setBusy(true); setError(null); setNotice(null);
    try { await rejectItem(item.elation_group_id, r); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function reprocess() {
    setBusy(true); setError(null); setNotice(null);
    try { await reprocessItem(item.elation_group_id); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const nameLine = [pt.last_name, pt.first_name].filter(Boolean).join(', ') || '(no name)';

  return (
    <div className="fax-review-card fax-signature-pending-card">
      <FaxImagePreview
        groupId={item.elation_group_id}
        imageId={item.first_image_id}
        alt={doc.one_line_summary || 'signature form'}
      />
      <div className="fax-review-card-body">
        <div className="fax-review-card-title">
          <span className="fax-sign-badge">Signature form</span>
          {' '}{item.subtype_label || 'Signature form'}
        </div>
        <div className="fax-review-card-meta">
          <strong>{nameLine}</strong>
          <span> — DOB {pt.dob || '?'}</span>
          {doc.one_line_summary && <span> · {doc.one_line_summary}</span>}
        </div>
        {signFormsDisabled ? (
          <div className="fax-signature-signer-status blocked">
            Sign-forms automation is off for this practice — sign this form in
            {' '}Elation, or turn on the Sign forms automation toggle.
          </div>
        ) : hasPlacement ? (
          <>
            {!hasSigner && reason && <div className="fax-review-card-reason">{reason}</div>}
            <div className="fax-signature-signer-status ready">
              ✓ Ready to sign — approving applies your on-file signature
              {hasSigner && (
                <span className="fax-signature-signer-id"> (chart signer #{item.signing_user_id} as fallback)</span>
              )}
            </div>
          </>
        ) : (
          <>
            {!hasSigner && reason && <div className="fax-review-card-reason">{reason}</div>}
            <div className="fax-signature-signer-status blocked">
              No signature location detected on this fax — review/sign manually in Elation.
            </div>
          </>
        )}

        <SignaturePlacementEditor
          groupId={item.elation_group_id}
          imageId={item.first_image_id}
          alt={doc.one_line_summary || 'signature form'}
          placement={placement}
          setPlacement={setPlacement}
          onSave={savePlacement}
        />
        {placeSaved && <div className="fax-review-card-notice">Signature position saved.</div>}
        {placeError && <div className="fax-review-card-error">Couldn’t save position: {placeError}</div>}

        {!signFormsDisabled && (
          <div className="fax-sign-preview">
            <button
              type="button"
              className="btn ghost sm"
              disabled={previewLoading || !hasPlacement}
              title={!hasPlacement ? 'No signature location detected — nothing to preview' : undefined}
              onClick={loadPreview}
            >
              {previewLoading ? 'Rendering…' : (previewUrl ? 'Refresh signed preview' : 'Preview signed PDF')}
            </button>
            {previewError && <div className="fax-review-card-error">Preview: {previewError}</div>}
            {previewUrl && (
              <>
                <iframe className="fax-sign-preview-frame" title="Signed form preview — exactly what Approve & sign will send" src={previewUrl} />
                <div className="fax-review-card-notice">
                  This is exactly what Approve &amp; sign will file and fax — check every signature/date stamp.
                  {' '}<a href={previewUrl} target="_blank" rel="noopener noreferrer">Open in a new tab ↗</a>
                </div>
              </>
            )}
          </div>
        )}

        {isAdmin && !signFormsDisabled && (
          <div className="fax-sign-allowlist">
            {allowDone ? (
              <div className="fax-review-card-notice">
                This form type will auto-sign going forward.
              </div>
            ) : (
              <button
                type="button"
                className="btn ghost sm"
                disabled={allowBusy || !subtypeKey}
                title={subtypeKey ? undefined : 'No form-type key on this item'}
                onClick={allowlist}
              >
                {allowBusy ? 'Saving…' : 'Auto-sign this form type from now on'}
              </button>
            )}
          </div>
        )}

        {notice && <div className="fax-review-card-notice">{notice}</div>}
        {error && <div className="fax-review-card-error">Error: {error}</div>}
      </div>
      <div className="fax-review-card-actions">
        <button
          className="btn accent sm"
          disabled={busy || !hasPlacement || signFormsDisabled}
          title={
            signFormsDisabled ? 'Sign-forms automation is off — sign manually in Elation'
              : (!hasPlacement ? 'No signature location detected — sign manually in Elation' : undefined)
          }
          onClick={approve}
        >
          Approve &amp; sign
        </button>
        <button
          className="btn ghost sm"
          disabled={busy}
          title="Re-run through triage under current automation settings"
          onClick={reprocess}
        >
          Reprocess
        </button>
        <button className="btn ghost sm" disabled={busy} onClick={reject}>Reject</button>
      </div>
    </div>
  );
}

// Review card for an rx_refill_pending item (Refill eRx Upgrade). A refill
// request couldn't auto-draft (2+ active matches, ambiguous resolution, or no
// physician on chart), so the doctor picks the right medication and Approve
// creates the unsigned eRx draft via the SAME approve route (Task 4), which
// special-cases suggested_classification === 'rx_refill_pending' and returns
// { ok, draftId } on success (NOT the approve_outcome shape). On a 400/409/500
// the row stays in review. We do NOT build a parallel approve path.
//
// The queue carries a refill_payload jsonb:
//   { reason, rxRefill:{drug,sig,qty,strength,days_supply,...},
//     candidates:[{medicationId,name,directions,qty,qtyUnits,refills,daysSupply,
//                  threadId,threadIsPermanent}],            // 2+-active-match case
//     options:[{label, product:{id,name,strength,isControlled}}], // ambiguous case
//     physicianMissing:bool }
// One of candidates/options is populated (or neither when physicianMissing /
// unresolved). NOTE the field rename: a candidate's `threadId` maps to the
// route's `selection.sourceThreadId` (and threadIsPermanent → sourceThreadIsPermanent).
export function FaxRefillPendingCard({ item, onChange }) {
  const extract = item.raw_extract || {};
  const pt = extract.patient || {};
  const doc = extract.document || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [chosen, setChosen] = useState(-1);

  const payload = item.refill_payload || {};
  const rxRefill = payload.rxRefill || {};
  const physicianMissing = !!payload.physicianMissing;
  const reason = item.validation_summary && item.validation_summary.reason;
  const drug = (rxRefill && rxRefill.drug) || 'Refill';

  // Build the single-select choice list. Field names in each `selection` MUST
  // match the Task 4 route exactly.
  const choices = [];
  const candidates = Array.isArray(payload.candidates) ? payload.candidates : [];
  for (const c of candidates) {
    if (!c) continue;
    choices.push({
      label: c.name,
      selection: {
        medicationId: c.medicationId,
        orderType: 'Refill',
        directions: c.directions,
        qty: c.qty,
        qtyUnits: c.qtyUnits,
        refills: c.refills,
        daysSupply: c.daysSupply,
        sourceThreadId: c.threadId,               // payload.threadId → route.sourceThreadId
        sourceThreadIsPermanent: c.threadIsPermanent,
      },
    });
  }
  const options = Array.isArray(payload.options) ? payload.options : [];
  for (const o of options) {
    if (!o || !o.product) continue;
    choices.push({
      label: o.label,
      selection: {
        medicationId: o.product.id,
        orderType: 'New',
        directions: rxRefill.sig,
        qty: rxRefill.qty,
        qtyUnits: rxRefill.qty_units,
        refills: rxRefill.refills,
        daysSupply: rxRefill.days_supply,
      },
    });
  }

  const hasChoice = chosen >= 0 && chosen < choices.length;

  async function approve() {
    // Guard: disabled until a choice is selected and never when no physician.
    if (!hasChoice || physicianMissing) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      // Same route the normal review card uses; refill branch returns
      // { ok, draftId } on a 2xx.
      const result = await approveRefill(item.elation_group_id, { selection: choices[chosen].selection });
      if (result && result.ok) {
        setNotice('Refill draft created in Elation — review & sign there.');
        onChange(); // refresh — the row drops off the queue.
      } else {
        // Defensive: a 2xx without ok shouldn't happen, but keep the item and
        // surface whatever came back.
        setError((result && result.error) || 'approve did not create the draft — still needs review');
        onChange();
      }
    } catch (e) {
      // 400 (no medication) / 409 (no physician) / 500: row stays in review on
      // the server. Surface the error; do NOT refresh it away.
      setError(e.message);
    } finally { setBusy(false); }
  }

  async function reject() {
    const r = window.prompt('Reject reason (optional)?', '');
    if (r === null) return;
    setBusy(true); setError(null); setNotice(null);
    try { await rejectItem(item.elation_group_id, r); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function reprocess() {
    setBusy(true); setError(null); setNotice(null);
    try { await reprocessItem(item.elation_group_id); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const nameLine = [pt.last_name, pt.first_name].filter(Boolean).join(', ') || '(no name)';

  return (
    <div className="fax-review-card fax-refill-pending-card">
      <FaxImagePreview
        groupId={item.elation_group_id}
        imageId={item.first_image_id}
        alt={doc.one_line_summary || 'refill request'}
      />
      <div className="fax-review-card-body">
        <div className="fax-review-card-title">
          <span className="fax-refill-badge">Refill request</span>
          {' '}{drug}
        </div>
        <div className="fax-review-card-meta">
          <strong>{nameLine}</strong>
          <span> — DOB {pt.dob || '?'}</span>
          {doc.one_line_summary && <span> · {doc.one_line_summary}</span>}
        </div>
        {reason && <div className="fax-review-card-reason">{reason}</div>}

        {physicianMissing && (
          <div className="fax-signature-signer-status blocked">
            No physician on chart — assign in Elation, then approve.
          </div>
        )}

        {choices.length > 0 ? (
          <div className="fax-refill-choices" role="radiogroup" aria-label="Pick the medication to refill">
            {choices.map((ch, i) => (
              <label className="fax-refill-choice" key={i}>
                <input
                  type="radio"
                  name={`refill-choice-${item.elation_group_id}`}
                  checked={chosen === i}
                  disabled={busy}
                  onChange={() => setChosen(i)}
                />
                <span className="fax-refill-choice-label">{ch.label || '(unnamed medication)'}</span>
              </label>
            ))}
          </div>
        ) : (
          !physicianMissing && (
            <div className="fax-signature-signer-status blocked">
              No matching medication to draft — resolve manually in Elation.
            </div>
          )
        )}

        {notice && <div className="fax-review-card-notice">{notice}</div>}
        {error && <div className="fax-review-card-error">Error: {error}</div>}
      </div>
      <div className="fax-review-card-actions">
        <button
          className="btn accent sm"
          disabled={busy || !hasChoice || physicianMissing}
          title={
            physicianMissing ? 'No physician on chart — assign in Elation first'
              : (!hasChoice ? 'Pick a medication first' : undefined)
          }
          onClick={approve}
        >
          Approve
        </button>
        <button
          className="btn ghost sm"
          disabled={busy}
          title="Re-run through triage under current automation settings"
          onClick={reprocess}
        >
          Reprocess
        </button>
        <button className="btn ghost sm" disabled={busy} onClick={reject}>Reject</button>
      </div>
    </div>
  );
}

// Review card for a "fillable_form" item (Form-Fill, Phase 4). Unlike the
// other cards, this one does NOT approve/file inline — the clinician confirms
// each extracted field in a full-screen form-fill SCREEN (opened via
// onOpenScreen) where Review & sign renders + signs + faxes the completed form
// back. Here we only surface the form + a launch button + Reject (the same
// shared rejectItem the other cards use).
export function FaxFormFillCard({ item, onChange, onOpenScreen }) {
  const extract = item.raw_extract || {};
  const pt = extract.patient || {};
  const doc = extract.document || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const reason = (item.validation_summary && item.validation_summary.reason) || null;
  const nameLine = [pt.last_name, pt.first_name].filter(Boolean).join(', ') || '(no name)';

  async function reject() {
    const r = window.prompt('Reject reason (optional)?', '');
    if (r === null) return;
    setBusy(true); setError(null); setNotice(null);
    try { await rejectItem(item.elation_group_id, r); onChange(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function reprocess() {
    // The re-run is DETACHED on the server (re-detect fields + placement takes
    // ~15-30s) and the item re-parks as the SAME fillable-form card — so without a
    // notice the click looks like a no-op. Surface progress + tell the clinician to
    // reopen form-fill once it's done, and refresh the queue after it's had time to
    // re-park.
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await reprocessItem(item.elation_group_id);
      setNotice(
        r && r.status === 'already_processing'
          ? 'Already reprocessing — give it ~30s, then reopen form-fill to see the updated placement.'
          : 'Reprocessing under current settings (~30s to re-detect fields + placement). Reopen form-fill once done to see the update.'
      );
      setTimeout(onChange, 30000);
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fax-review-card fax-form-fill-card">
      <FaxImagePreview
        groupId={item.elation_group_id}
        imageId={item.first_image_id}
        alt={doc.one_line_summary || 'fillable form'}
      />
      <div className="fax-review-card-body">
        <div className="fax-review-card-title">
          <span className="fax-form-badge">Fillable form</span>
          {' '}{item.subtype_label || 'Fillable form'}
        </div>
        <div className="fax-review-card-meta">
          <strong>{nameLine}</strong>
          <span> — DOB {pt.dob || '?'}</span>
          {doc.one_line_summary && <span> · {doc.one_line_summary}</span>}
        </div>
        {reason && <div className="fax-review-card-reason">{reason}</div>}
        {notice && <div className="fax-review-card-notice">{notice}</div>}
        {error && <div className="fax-review-card-error">Error: {error}</div>}
      </div>
      <div className="fax-review-card-actions">
        <button
          className="btn accent sm"
          disabled={busy}
          onClick={() => onOpenScreen(item.elation_group_id)}>
          Open form-fill
        </button>
        <button
          className="btn ghost sm"
          disabled={busy}
          title="Re-run through triage under current automation settings"
          onClick={reprocess}
        >
          Reprocess
        </button>
        <button className="btn ghost sm" disabled={busy} onClick={reject}>Reject</button>
      </div>
    </div>
  );
}

// Adjustable placement overlay. Renders the first-page image with draggable +
// resizable boxes for each placement entry's signatureBox (and dateBox, when
// present). Geometry is normalized [0,1]; boxes are positioned with percentage
// CSS. Drag/resize clamps every box to [0,1] with x+w<=1 / y+h<=1, then calls
// onSave(nextPlacement) on pointer-up ONLY (never mid-drag).
//
// LIMITATION: the queue item carries only first_image_id (page 1). Entries on
// other pages can't be shown (no image id to render them over); they're left
// untouched in the saved array and a note is shown. The stored geometry for
// those pages is preserved exactly (we only ever replace the page-1 entries).
function SignaturePlacementEditor({ groupId, imageId, alt, placement, setPlacement, onSave }) {
  const wrapRef = useRef(null);

  // Indices of placement entries that live on the page we can render (page 1).
  const onPage1 = [];
  let offPageCount = 0;
  for (let i = 0; i < placement.length; i++) {
    if (Number(placement[i] && placement[i].page) === 1) onPage1.push(i);
    else offPageCount++;
  }

  // Update one box (signatureBox|dateBox) of one placement entry, normalized.
  // `commit` => persist on pointer-up. We rebuild the whole array so off-page
  // entries are carried through unchanged.
  const update = useCallback((idx, key, box, commit) => {
    setPlacement(prev => {
      const next = prev.map((entry, i) => {
        if (i !== idx) return entry;
        return { ...entry, [key]: normalizeBox(box) };
      });
      if (commit) onSave(next);
      return next;
    });
  }, [setPlacement, onSave]);

  if (!imageId) {
    return null; // nothing to render an overlay on
  }
  if (placement.length === 0) {
    return null; // no boxes to place — show nothing (don't crash)
  }

  return (
    <div className="fax-sign-placement">
      <div className="fax-sign-placement-hint">
        Drag to move · drag the corner to resize · saves on release
      </div>
      <FaxEditablePagePreview groupId={groupId} imageId={imageId} alt={alt} wrapperRef={wrapRef}>
        {onPage1.map(idx => {
          const entry = placement[idx] || {};
          const sig = entry.signatureBox;
          const date = entry.dateBox;
          return (
            <React.Fragment key={idx}>
              {sig && (
                <PlacementBox
                  label="Signature"
                  kind="sig"
                  box={normalizeBox(sig)}
                  wrapRef={wrapRef}
                  onMove={(b, commit) => update(idx, 'signatureBox', b, commit)}
                />
              )}
              {date && (
                <PlacementBox
                  label="Date"
                  kind="date"
                  box={normalizeBox(date)}
                  wrapRef={wrapRef}
                  onMove={(b, commit) => update(idx, 'dateBox', b, commit)}
                />
              )}
            </React.Fragment>
          );
        })}
      </FaxEditablePagePreview>
      {offPageCount > 0 && (
        <div className="fax-sign-placement-note">
          {offPageCount} signature {offPageCount === 1 ? 'box is' : 'boxes are'} on
          {' '}other pages and can’t be adjusted here (only page 1 is shown). Their
          {' '}stored position is preserved.
        </div>
      )}
    </div>
  );
}

// A single draggable + resizable box. Pixel deltas are converted to normalized
// deltas using the wrapper's getBoundingClientRect(). The box body drags
// (move); the bottom-right handle resizes. Both clamp to [0,1] with
// x+w<=1 / y+h<=1 and persist on pointer-up via onMove(box, /*commit*/true).
function PlacementBox({ label, kind, box, wrapRef, onMove }) {
  const dragRef = useRef(null);

  // Begin a move or resize gesture. mode: 'move' | 'resize'.
  const start = useCallback((mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = wrapRef.current && wrapRef.current.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...box },
      rectW: rect.width,
      rectH: rect.height,
    };

    // Compute the next normalized box for a given pointer event.
    const compute = (ev) => {
      const d = dragRef.current;
      const dx = (ev.clientX - d.startX) / d.rectW;
      const dy = (ev.clientY - d.startY) / d.rectH;
      if (d.mode === 'move') {
        return {
          x: clamp(d.orig.x + dx, 0, 1 - d.orig.w),
          y: clamp(d.orig.y + dy, 0, 1 - d.orig.h),
          w: d.orig.w,
          h: d.orig.h,
        };
      }
      // resize from bottom-right; keep top-left fixed.
      return {
        x: d.orig.x,
        y: d.orig.y,
        w: clamp(d.orig.w + dx, MIN_BOX, 1 - d.orig.x),
        h: clamp(d.orig.h + dy, MIN_BOX, 1 - d.orig.y),
      };
    };

    const onPointerMove = (ev) => {
      if (!dragRef.current) return;
      onMove(compute(ev), false); // live update, not persisted
    };

    const onPointerUp = (ev) => {
      const d = dragRef.current;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      if (!d) return;
      const next = compute(ev);
      dragRef.current = null;
      onMove(next, true); // persist on release
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }, [box, wrapRef, onMove]);

  const style = {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };

  return (
    <div
      className={`fax-sign-box fax-sign-box-${kind}`}
      style={style}
      onPointerDown={(e) => start('move', e)}
    >
      <span className="fax-sign-box-label">{label}</span>
      <span
        className="fax-sign-box-handle"
        onPointerDown={(e) => start('resize', e)}
        aria-label={`Resize ${label} box`}
      />
    </div>
  );
}
